import { useState } from 'react'
import { Clock, PhoneDisabled, Prohibition, Reply, Sparks, WarningTriangle } from 'iconoir-react'
import moment from 'moment-timezone'
import { Button } from 'app/components/ui/button'
import { SendLaterButton } from 'app/components/sms-scheduling/SendLaterButton'
import { heldOutcome } from 'app/lib/heldMessages'
import { sendAtLabel } from 'app/lib/scheduledSend'
import type { HeldMessage, HeldNotSentVariant } from 'app/types/held-message'
import { bg } from 'lib/background'

type Tone = { band: string; icon: string; headline: string }

/**
 * A healthy queued message. Primary as an accent, not a fill: a solid tinted
 * slab competes with the amber state next to it, and the whole point of that
 * state is to be the only thing in the tray that raises its voice. The left
 * edge carries the colour so the body text can sit on an ordinary surface and
 * use ordinary text roles.
 */
const QUEUED_TONE: Tone = {
  band: `border-default border-l-4 border-l-primary-500 bg-surface-raised`,
  icon: `text-primary-600`,
  headline: `text-strong`,
}

/**
 * Amber, and only here. It means "this needs a decision from you", which is true
 * of exactly one not-sent state: they replied, and the user still has to choose
 * whether the message goes out. Spending it on the closed states as well (opted
 * out, no number on file) left nothing in the tray reading as urgent.
 */
const ATTENTION_TONE: Tone = {
  band: `border-warning-default bg-warning-subtle`,
  icon: `text-warning-default`,
  headline: `text-warning-strong`,
}

/** A closed fact: say what happened, ask nothing, draw no eye. */
const NEUTRAL_TONE: Tone = {
  band: `border-default bg-surface-subtle`,
  icon: `text-subtle`,
  headline: `text-strong`,
}

/**
 * How each not-sent situation presents. The copy lives here rather than in
 * `heldOutcome` because it is tray phrasing: a full headline that stands on its
 * own, where the Scheduled list wants the short "Not sent. <reason>" fragment
 * from the same outcome.
 *
 * No contact name in any of it. The thread header already names the person and
 * the Scheduled row carries the name as its own field, and on a shared tracking
 * number inbound attribution is known-unreliable, so naming here could pin a
 * compliance-adjacent message on the wrong patient.
 *
 * Note what this map does NOT decide: whether a send is offered. That is
 * `canSendAnyway` on the outcome, so there is one answer rather than two.
 */
const NOT_SENT_STATES: Record<HeldNotSentVariant, { Icon: typeof Clock; headline: string; tone: Tone }> = {
  replied: { Icon: Reply, headline: `They replied, so this was not sent`, tone: ATTENTION_TONE },
  opted_out: { Icon: Prohibition, headline: `They opted out, so this was not sent`, tone: NEUTRAL_TONE },
  no_phone: { Icon: PhoneDisabled, headline: `No cell number on file, so this was not sent`, tone: NEUTRAL_TONE },
  // Sparks, because a sparkle is this app's AI mark (see AISparkle). Neutral, not amber:
  // the agent now owns the conversation, so the patient is being answered and nothing is
  // being asked of the user.
  agent_active: {
    Icon: Sparks,
    headline: `The AI Agent took over, so this was not sent`,
    tone: NEUTRAL_TONE,
  },
  unknown: { Icon: WarningTriangle, headline: `This message was not sent`, tone: NEUTRAL_TONE },
}

export type ScheduledTrayProps = {
  held: HeldMessage[]
  timezone: string
  /**
   * The recipient has opted out of SMS right now. Suppresses "Send anyway",
   * which decides whether to appear from the row's STORED `errorCode`: a row
   * cancelled with `CONTACT_REPLIED` keeps offering it after that same reply
   * turned out to be STOP, which is the likeliest ordering of the two. The rest
   * of the tray stays, because a queued message to that number still has to be
   * cancellable.
   */
  optedOut?: boolean
  /**
   * The AI agent is replying on this thread. It withholds both ways of putting
   * a new message out, the reschedule picker and "Send anyway", while leaving
   * the row and its Cancel: a message queued before the agent took over still
   * has to be visible and cancellable, and hiding it would strand it.
   */
  agentHandling?: boolean
  /**
   * A reschedule is already in flight for this thread's `useScheduledSend`.
   * Reschedule is cancel-then-send, so a second click here while one is
   * running can interleave two cancel/send pairs: either two messages queued
   * at once, breaking the one-per-thread cap, or a cancel from the second
   * click landing after the first click's resend and leaving nothing queued.
   * Disabling the picker while this is true keeps a second click from ever
   * starting.
   */
  scheduling?: boolean
  onCancel: (id: string) => void
  onSendAnyway: (message: HeldMessage) => Promise<boolean>
  onReschedule: (id: string, text: string, sendAtLocal: string) => void
  /**
   * Marks a not-sent notice seen, for this user and every other one. Resolves
   * false when the write failed, which leaves the row on screen rather than
   * hiding something that will be back on the next load.
   */
  onDismiss: (id: string) => Promise<boolean>
}

type HeldBandProps = Omit<ScheduledTrayProps, `held` | `onDismiss`> & {
  message: HeldMessage
  /** Other queued rows this band stands in for, named in its footer. */
  others: number
  onDismiss: (id: string) => void
}

/** One row of the tray: a queued message, or a notice that one did not send. */
function HeldBand({
  message,
  others,
  timezone,
  optedOut,
  agentHandling,
  scheduling,
  onCancel,
  onSendAnyway,
  onReschedule,
  onDismiss,
}: HeldBandProps) {
  const outcome = heldOutcome(message)
  const notSent = outcome.kind === `not_sent`
  // Presence is not validity: moment-timezone resolves an unrecognized zone name
  // as UTC without complaint, so a bogus zone would print a confident time hours
  // off the practice clock and offer a picker built on it. Every write path
  // checks `moment.tz.zone` for that reason, and so does this one.
  const zoneKnown = !!moment.tz.zone(timezone)
  const sendAtLocal =
    message.sendDate && zoneKnown ? moment.tz(message.sendDate, timezone).format(`YYYY-MM-DDTHH:mm`) : undefined

  const handleSendAnyway = async () => {
    const sent = await onSendAnyway(message)
    if (sent) onDismiss(message.id)
  }

  const state = outcome.kind === `not_sent` ? NOT_SENT_STATES[outcome.variant] : undefined
  const tone = state?.tone ?? QUEUED_TONE
  const Icon = state?.Icon ?? Clock
  const headline =
    state?.headline ?? `Sends ${(sendAtLocal && sendAtLabel(sendAtLocal, timezone)) || `at a later time`}`

  return (
    // The inset tracks the composer form's `px-4 md:px-6` below it, so the band
    // and the message box share an edge at both breakpoints. Change one and the
    // other has to follow.
    <div className={`mx-4 rounded-md border px-3 py-2.5 md:mx-6 ${tone.band}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${tone.icon}`} strokeWidth={2} />
        <span className={`flex-1 text-body-sm font-medium ${tone.headline}`}>{headline}</span>
        {notSent ? (
          <>
            {/*
              Still gated on `canSendAnyway` and the live opt-out, unchanged. The
              variant decides how this row looks, never whether a send exists.
              "Send anyway" outweighs "Dismiss" because it is the choice being
              asked for; dismissing is the way out.
            */}
            {outcome.canSendAnyway && !optedOut && !agentHandling && (
              <Button type="button" variant="secondary" size="sm" onClick={() => bg(() => handleSendAnyway())}>
                Send anyway
              </Button>
            )}
            {/* "Dismiss", not "Discard": this hides the notice in local state and deletes nothing. */}
            <Button type="button" variant="subtle" size="sm" onClick={() => onDismiss(message.id)}>
              Dismiss
            </Button>
          </>
        ) : (
          <>
            {sendAtLocal && !agentHandling && (
              <SendLaterButton
                timezone={timezone}
                disabled={scheduling}
                appearance="inline"
                onSchedule={(next) => onReschedule(message.id, message.text, next)}
              />
            )}
            <Button type="button" variant="subtle" size="sm" onClick={() => onCancel(message.id)}>
              Cancel
            </Button>
          </>
        )}
      </div>
      {/* Quoted and ruled, because otherwise the draft reads as the tray talking:
          "Sends Tomorrow 8:00 AM" followed by a bare line of prose looks like a
          second sentence from the UI rather than the text that will go out. A
          rule rather than a fill, so it sits on the amber and neutral tones too,
          and in dark mode. */}
      <p className="mt-1.5 border-l-2 border-default pl-2 text-body-sm text-default">
        <q>{message.text}</q>
      </p>
      {others > 0 && <p className="mt-1.5 text-label-xs text-subtle">{others} more scheduled on this conversation</p>}
    </div>
  )
}

/**
 * A queued message, shown between the last message and the composer.
 *
 * Deliberately not a bubble in the timeline. A timeline is a record of what was
 * said, and this has not been said. In a shared team inbox the cost of a
 * colleague reading a queued message as sent is high, so it sits outside the
 * conversation and reads as an outbox instead.
 */
export function ScheduledTray({
  held,
  timezone,
  optedOut,
  agentHandling,
  scheduling,
  onCancel,
  onSendAnyway,
  onReschedule,
  onDismiss,
}: ScheduledTrayProps) {
  // Hides the row the moment the user acts, without waiting for the mutation
  // and the refetch behind it. `read` is what actually keeps it hidden, on the
  // next load and for everyone else; this only covers the gap in between.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const dismiss = async (id: string) => {
    if (await onDismiss(id)) setDismissedIds((prev) => new Set(prev).add(id))
  }

  const visible = held.filter((m) => {
    if (dismissedIds.has(m.id)) return false
    // A queued row is unread too, so `read` only decides a not-sent one.
    if (m.status === `pending`) return true
    return !m.read
  })
  if (!visible.length) return null

  // Soonest first within each group. A cancelled or failed row keeps its
  // original, now-past sendDate forever, which is why the two groups are sorted
  // separately rather than together.
  //
  // Invariant: ContactInbox withholds the composer's own SendLaterButton
  // whenever ANY row here is pending (`hasQueued`). That holds because a queued
  // row is always rendered when one exists, so the tray's own picker is offered
  // at exactly the moment the composer's is withheld.
  const bySoonest = (a: HeldMessage, b: HeldMessage) => (a.sendDate ?? ``).localeCompare(b.sendDate ?? ``)
  // Two different kinds of thing, so two bands rather than a contest one of them
  // loses. A not-sent notice reports something that already happened and needs
  // acknowledging; a queued row is a plan the user can still change. Showing
  // only the winner meant a failure notice vanished the moment anything was
  // queued on the same thread, and the user never learned their message had not
  // gone out.
  const notSentRows = visible.filter((m) => m.status !== `pending`).sort(bySoonest)
  const queuedRows = visible.filter((m) => m.status === `pending`).sort(bySoonest)
  // Counts queued rows only, which is the thing the sentence names. It used to
  // count every row in the tray, so a cancelled message was announced as "1 more
  // scheduled" when scheduled is exactly what it is not.
  const others = Math.max(queuedRows.length - 1, 0)
  // Notices first, nearest the conversation they are about; the queued band sits
  // last, against the composer, because it is the one that is still a plan.
  const bandProps = { timezone, optedOut, agentHandling, scheduling, onCancel, onSendAnyway, onReschedule }
  return (
    <div className="flex flex-col gap-2">
      {notSentRows.map((row) => (
        <HeldBand key={row.id} message={row} others={0} onDismiss={(id) => bg(() => dismiss(id))} {...bandProps} />
      ))}
      {queuedRows[0] && (
        <HeldBand
          key={queuedRows[0].id}
          message={queuedRows[0]}
          others={others}
          onDismiss={(id) => bg(() => dismiss(id))}
          {...bandProps}
        />
      )}
    </div>
  )
}
